// What a box is doing RIGHT NOW, as the monitor beat reports it — the disk
// hygiene rollup and the wider live stats payload.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { UnitMemoryPostureSchema } from './memory-posture';

/**
 * The box's LAST disk RECLAIM — the steps only a box short of disk may take, beyond the
 * routine sweep: the in-band floor reclaim of an assignment's `before` pass
 * (`disk_floor`, under `DZ_HYGIENE_MIN_FREE_GB`) and the idle cadence sweep's unused-image
 * step under the pressure line (`disk_pressure`). Rides {@link BoxHygieneStatsSchema} so
 * an operator reads "unused images pruned, N GiB freed" on `box fleet_status` without
 * opening a task timeline. Counts and bytes only — never a path, an image or a reason
 * (those are in the run's `hygiene_summary` event and the box log).
 */
export const BoxHygieneReclaimSchema = z.object({
  /** Which line the box was under when it reclaimed. */
  trigger: z.enum(['disk_floor', 'disk_pressure']),
  /** When the reclaim ran (ms since epoch, the box's clock). */
  atMs: z.number().int().nonnegative(),
  /** Free-disk delta across the reclaim's own steps (`0` when unobservable). */
  bytesFreed: z.number().int().nonnegative(),
  /** Steps it took or refused. */
  actions: z.number().int().nonnegative(),
  /**
   * The unused-image prune (`docker image prune -a -f`): `pruned`, `refused` (a container
   * was running, docker could not answer, the box was not under the line), `dry_run`, or
   * `not_reached` (a cheaper step was enough, or the step is switched off).
   */
  unusedImages: z.enum(['pruned', 'refused', 'dry_run', 'not_reached']),
});
export type BoxHygieneReclaim = z.infer<typeof BoxHygieneReclaimSchema>;

/**
 * The fault kinds a box knows it cannot recover from by itself
 * (`apps/runner/src/box-faults.ts`), each raised once and cleared by the box when the cause
 * is gone:
 *
 *   disk_unreclaimable  the before-pass refused an assignment for disk AFTER its own clean
 *                       and reclaim (`disk_floor_unmet`, the floor or the forecast). The box
 *                       is OUT OF ROTATION while it stands ({@link BOX_FAULTS_OUT_OF_ROTATION}):
 *                       every assignment it took would fail the same way;
 *   tree_unreleasable   a per-PR tree's lease could not be released after repeated tries
 *                       (`apps/runner/src/tree-lease-reconcile.ts`). Reported; the box keeps
 *                       working — one stuck tree costs disk, which the first kind guards.
 */
export const BOX_FAULT_KINDS = ['disk_unreclaimable', 'tree_unreleasable'] as const;
export type BoxFaultKind = (typeof BOX_FAULT_KINDS)[number];

/** The kinds that take a box out of rotation: the assigner offers it no work while one stands. */
export const BOX_FAULTS_OUT_OF_ROTATION: ReadonlySet<string> = new Set<BoxFaultKind>([
  'disk_unreclaimable',
]);

/**
 * One standing fault on the beat. `kind` is a bounded STRING, not the enum, on purpose: a
 * mothership must decode a beat from a newer runner carrying a kind it does not know yet —
 * an undecodable presence value reads as an ABSENT box — and a reader acts only on the kinds
 * it lists ({@link BOX_FAULT_KINDS}).
 */
export const BoxFaultSchema = z.object({
  kind: z.string().min(1).max(40),
  /** When the box first raised it (ms since epoch, the box's clock). */
  sinceMs: z.number().int().nonnegative(),
  /** What the box saw, for the alert — never a secret, a path at most. */
  detail: z.string().max(300),
});
export type BoxFault = z.infer<typeof BoxFaultSchema>;

/**
 * Counters from a box's LAST boot-time env-hygiene sweep (plan 102), folded into the
 * presence beat's {@link BoxStats}. Boot hygiene (the heavy local GC — stale checkouts,
 * dangling docker, leaked temps) runs before any task, so it has no `run_id` and thus
 * no `fleet.evt.<run_id>` channel; unlike the per-task passes (which stream a
 * `hygiene_summary` status event), these counters are the sweep's ONLY signal upstream.
 * COUNT-ONLY by design — the KV value stays small; the full finding list lives in the
 * box's local log. Its PRESENCE means a real, enabled sweep ran, so all-zero counters
 * read as "clean box", never "no data" (a disabled / unassembled pass omits it).
 */
export const BoxHygieneStatsSchema = z.object({
  /** Bytes reclaimed by the sweep — best-effort free-disk delta across its act phase. */
  bytesFreed: z.number().int().nonnegative(),
  /** Findings the sweep surfaced (stale checkouts, dangling docker, leaked temps/pgids). */
  findings: z.number().int().nonnegative(),
  /** Destructive actions the sweep took. */
  actions: z.number().int().nonnegative(),
  /** Errors the sweep recorded (folded into its summary, never thrown). */
  errors: z.number().int().nonnegative(),
  /**
   * Findings the sweep REFUSED to act on — confinement, not ours, a throwing seam
   * (plan 101 runner lifecycle, slice 07). The fleet printed the same 67 findings
   * every boot with nothing saying which forty were refused; this is that number.
   */
  refused: z.number().int().nonnegative().optional(),
  /** Distinct refusal reasons, most frequent first, bounded (8 x 160 chars) — a beat, not a log. */
  refusedReasons: z.array(z.string().max(160)).max(8).optional(),
  /**
   * The box's last disk reclaim ({@link BoxHygieneReclaimSchema}) — kept beside the
   * sweep's counters rather than replacing them, because a floor reclaim is a few steps
   * and the sweep is the whole box. Absent until one runs; a beat from an older runner
   * never carries it, and an older mothership strips it (`z.object`, never `.strict()`).
   */
  reclaim: BoxHygieneReclaimSchema.optional(),
});
export type BoxHygieneStats = z.infer<typeof BoxHygieneStatsSchema>;

/**
 * The box's own DISK FORECAST (2026-11-02, owner: "dz should have predicted this") — a
 * least-squares fit over its recent workspace free-disk samples (`apps/runner`
 * `hygiene/disk-forecast.ts`). worker-3-vm-2 went from 13.7 GB free to 0.7 GB within one
 * run while every fixed line read healthy when the run started.
 *
 *   - `filling`: free disk is falling at `bytesPerHour` (negative) and runs out in
 *     `hoursToFull` at that rate, from `freeBytes` at the last sample;
 *   - `not_filling`: flat or rising (a reclaim, a finished build);
 *   - `insufficient_history`: too few samples or too short a span to say — never a verdict.
 *
 * Beat-only and optional both ways: an older runner never sends it, an older mothership
 * strips it (`z.object`).
 */
export const BoxDiskForecastSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('filling'),
    hoursToFull: z.number().nonnegative(),
    bytesPerHour: z.number(),
    freeBytes: z.number().int().nonnegative(),
  }),
  z.object({ kind: z.literal('not_filling'), bytesPerHour: z.number() }),
  z.object({ kind: z.literal('insufficient_history'), samples: z.number().int().nonnegative() }),
]);
export type BoxDiskForecast = z.infer<typeof BoxDiskForecastSchema>;

/**
 * The qemu-guest-agent inside a libvirt guest (2026-11-02): `active` lets the HOST read the
 * VM's filesystems and memory; `inactive` is a guest whose agent could not be started;
 * `no_channel` / `no_systemd` is a box where it does not apply (not a configured libvirt
 * guest). Converged at boot by `apps/runner` `guest-agent.ts`; beat-only, optional both ways.
 */
export const BoxGuestAgentSchema = z.enum(['active', 'inactive', 'no_channel', 'no_systemd']);
export type BoxGuestAgent = z.infer<typeof BoxGuestAgentSchema>;

/**
 * Live box telemetry a runner stamps into its presence beat — arc 101 slice 04.
 * The BEAT rides the `FLEET_PRESENCE` KV value (90s TTL): a point-in-time load hint
 * the load-aware assigner (#291) and `box_status` read live. Since #6555 a SUBSET is
 * also durable: `cron.runner_stat_sampler` copies `loadAvg1`, `freeMemBytes` and the
 * `workspaceDisk` pair into `runner_stat_samples` once a minute (8 days, `@developerz/db`
 * `schema/fleet.ts`). Every other field stays beat-only. Complements the static
 * {@link BoxSpecs} — specs say how big the box is, stats say how busy it is right now.
 */
export const BoxStatsSchema = z.object({
  /** 1-minute load average (`os.loadavg()[0]`) — the coarse busyness signal. */
  loadAvg1: z.number().nonnegative(),
  /**
   * TRUE CPU busy % over the box's last completed 30 s window (#6738), from `/proc/stat`
   * tick deltas (`apps/runner/src/cpu-sample.ts`): `100 * (dTotal - dIdle) / dTotal`.
   * `iowait` counts as IDLE (a processor waiting on disk is free to run another job) and
   * `steal` as BUSY (the hypervisor took it); `guest`/`guest_nice` are inside `user`/`nice`
   * and never added again. In [0, 100] by construction — a window with any negative
   * per-field delta reads `null`, it is never clamped.
   *
   * ABSENT from a beat sent by a runner older than #6738 (an older mothership strips it,
   * `z.object`). `null` on the first beat after boot (no window has completed yet) and where
   * `/proc/stat` is unreadable — never `0`, which would read a broken gauge as an idle box.
   * Beat-only: not in `runner_stat_samples` (that column is #6569's remainder), and
   * `loadAvg1` stays the machine verdict's CPU signal.
   */
  cpuBusyPct: z.number().min(0).max(100).nullable().optional(),
  /**
   * AVAILABLE RAM in bytes (`os.freemem()`). Under Bun on Linux that is `/proc/meminfo`
   * `MemAvailable`, not `MemFree` — measured 2026-11-02, byte-identical — so it counts
   * reclaimable page cache as free, and `specs.memBytes - freeMemBytes` is memory used
   * EXCLUDING page cache (the fleet VMs: ~1 GB used of 20 GB, the rest cache). Pinned by
   * `apps/runner/src/stats.mem-available.test.ts`.
   */
  freeMemBytes: z.number().int().nonnegative(),
  /**
   * Free root-filesystem bytes; null when `statfs` is unavailable. Sampled on `/`, NOT on
   * the filesystem checkouts live on — the box's disk-floor refusal reads
   * {@link BoxStatsSchema.shape.workspaceDisk}, and so does the claim gate.
   */
  freeDiskBytes: z.number().int().nonnegative().nullable(),
  /**
   * The disk-floor pair, measured where the box REFUSES (#4094): free bytes on the
   * workspace path (`~/workspace-ai`, the root `scanFreeDisk` gauges) and the box's own
   * refusal floor (`DZ_HYGIENE_MIN_FREE_GB`, `0` when the floor or the whole hygiene pass
   * is off). Both halves ride together because the floor is runner-side configuration the
   * mothership has no other way to know, and a free number without its floor is not a
   * verdict. Graded by {@link boxDiskFloorVerdict}. `totalBytes` is the same filesystem's
   * size, a display denominator only: {@link boxDiskFloorVerdict} does not read it.
   *
   * ABSENT from a beat sent by an older runner, and absence is `not_reported`, never
   * "below the floor" — the assigner claims for such a box exactly as before.
   */
  workspaceDisk: z
    .object({
      /** Available bytes on the workspace filesystem; null when `statfs` cannot read it. */
      freeBytes: z.number().int().nonnegative().nullable(),
      /**
       * Total bytes of the workspace filesystem (`blocks * bsize` from the same `statfs` on
       * the workspace path as `freeBytes`); null when `statfs` cannot read it. Absent from a
       * beat sent by a runner older than #6554, which a reader shows as free bytes only.
       */
      totalBytes: z.number().int().nonnegative().nullable().optional(),
      /** The box's refusal floor in bytes; `0` = no floor. */
      floorBytes: z.number().int().nonnegative(),
      /**
       * WHY `freeBytes` is null, present exactly when the box's `statfs` on the workspace
       * path threw: `code` is the errno (`ENOENT` = the workspace root does not exist,
       * `EACCES`), or `unknown` for a throw that carried none. Ops 2026-11-03: nine boxes
       * beat a bare `null` for eleven days over a missing `~/workspace-ai`, which read
       * exactly like a platform with no `statfs`. Optional: absent from an older runner's
       * beat, and stripped by an older mothership (`z.object`). Display only — no verdict
       * reads it, so a null free number still grades `not_reported`.
       */
      unreadable: z
        .object({ kind: z.literal('probe_failed'), code: z.string().min(1).max(64) })
        .optional(),
    })
    .optional(),
  /**
   * The runner PROCESS's peak resident set in whole MiB
   * (`process.resourceUsage().maxRSS`) — a lifetime high-water mark, so it only
   * rises; `null` when unreadable. The coding agent's children (`bin/check`, `tsc`,
   * `git`) are separate processes and are NOT in it, which is why an 8 GB box can
   * report a small number here and still lose a `git push` to exit 137 (plan 101
   * runner lifecycle, slice 07). Absent from a beat sent by an older box.
   */
  rssPeakMb: z.number().int().nonnegative().nullable().optional(),
  /**
   * Counters from the box's last boot-time hygiene sweep (plan 102). Absent until a
   * box runs one (or when `DZ_HYGIENE` is off / the pass couldn't be assembled) — its
   * presence always reflects a real, enabled sweep. {@link BoxHygieneStatsSchema}.
   */
  hygiene: BoxHygieneStatsSchema.optional(),
  /**
   * Can this box REPLACE its own `nats.creds` (plan 101 slice 03)? Graded once at
   * boot by writing a probe file into the profile dir (`apps/runner`
   * `reportProfileWritability`) and folded into every beat unchanged. `false` is
   * the 2026-08-28 outage shape: the credential roll cannot persist, and the box
   * loses the fleet plane at its current `exp`. ABSENT when the box holds no
   * credential (local dev) — never defaulted, "no data" must not read as healthy.
   */
  credsWritable: z.boolean().optional(),
  /**
   * Can this box READ and replace its own `/etc/dz-runner/env`? `false` is the
   * 2026-09-02 fleet shape (`600 root:root` on 5/5 probed boxes under a unit
   * running as the service user): the env converge that purges retired keys and
   * fills the owed lines (`DZ_RUNNER_ORG`, `PATH`, `SENTRY_DSN`) cannot run, and
   * the fix is one `chown` on the box. ABSENT when there is no env file at all.
   */
  envWritable: z.boolean().optional(),
  /**
   * The runner's own unit memory bounds as read from its cgroup (epic #4739 slice 1),
   * graded by `unitMemoryPostureDefects`. ABSENT from an older binary or when the
   * cgroup is unreadable, and absence is never a defect. {@link UnitMemoryPostureSchema}.
   */
  unitMemory: UnitMemoryPostureSchema.optional(),
  /** The workspace disk's forecast ({@link BoxDiskForecastSchema}); absent from an older runner. */
  diskForecast: BoxDiskForecastSchema.optional(),
  /** The guest agent's state ({@link BoxGuestAgentSchema}); absent from an older runner. */
  guestAgent: BoxGuestAgentSchema.optional(),
  /** Standing faults the box cannot recover by itself ({@link BoxFaultSchema}); absent ⇒ none. */
  faults: z.array(BoxFaultSchema).max(8).optional(),
});
export type BoxStats = z.infer<typeof BoxStatsSchema>;

/**
 * Does this beat PROVE the box is below its own disk floor (#4094)? Pure and total.
 *
 * `below_floor` is the only verdict that withholds work, and it needs both halves of
 * {@link BoxStatsSchema.shape.workspaceDisk} read and a floor that is on. Every other
 * outcome is a distinct reason the beat proves nothing — an older runner
 * (`not_reported`), an unreadable workspace (`unobservable`), a disabled floor
 * (`floor_off`) — and the caller treats all of them as claimable: the box-side
 * refusal and the #5177 exclusion still backstop a box this could not see.
 *
 * The comparison is the box's own (`scanFreeDisk`: refuse on `free < floor`), so a box
 * sitting exactly at its floor is `clear` here and takes the work there.
 */
export type BoxDiskFloorVerdict =
  | { readonly kind: 'below_floor'; readonly freeBytes: number; readonly floorBytes: number }
  | { readonly kind: 'clear' }
  | { readonly kind: 'not_reported' }
  | { readonly kind: 'unobservable' }
  | { readonly kind: 'floor_off' };

export function boxDiskFloorVerdict(stats: BoxStats | null): BoxDiskFloorVerdict {
  const disk = stats?.workspaceDisk;
  if (disk === undefined) return { kind: 'not_reported' };
  if (disk.floorBytes <= 0) return { kind: 'floor_off' };
  if (disk.freeBytes === null) return { kind: 'unobservable' };
  if (disk.freeBytes >= disk.floorBytes) return { kind: 'clear' };
  return { kind: 'below_floor', freeBytes: disk.freeBytes, floorBytes: disk.floorBytes };
}

/** The standing fault that takes this box out of rotation, or `null`. Pure. */
export function boxOutOfRotationFault(stats: BoxStats | null): BoxFault | null {
  return stats?.faults?.find((f) => BOX_FAULTS_OUT_OF_ROTATION.has(f.kind)) ?? null;
}
